import React from "react";
import { Upload, X } from "lucide-react";

export const EMPTY_TITLE = {
  title: "", type: "movie", genres: "", year: 2024, rating: "PG-13",
  duration_min: 120, tmdb_rating: 7.0, description: "",
  poster: "", backdrop: "", trailer_yt: "", stream_url: "", cast: "",
  subtitles: [], audio_tracks: [],
};

export const EMPTY_EPISODE = { season: 1, episode_number: 1, title: "", description: "", duration_min: 30, stream_url: "", thumbnail: "" };

export const LANGS = [
  { code: "en", label: "English" }, { code: "es", label: "Spanish" }, { code: "fr", label: "French" },
  { code: "de", label: "German" }, { code: "it", label: "Italian" }, { code: "pt", label: "Portuguese" },
  { code: "hi", label: "Hindi" }, { code: "ja", label: "Japanese" }, { code: "ko", label: "Korean" },
  { code: "zh", label: "Chinese" }, { code: "ar", label: "Arabic" }, { code: "ru", label: "Russian" },
];

export const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function Modal({ children, onClose, title }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-[#141414] border border-white/10 rounded-lg p-8 max-w-2xl w-full my-8" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-semibold">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function FileField({ label, url, onUrl, loading, onFile, accept, testid }) {
  const [drag, setDrag] = React.useState(false);
  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDrag(false);
    const f = e.dataTransfer?.files?.[0];
    if (!f) return;
    if (accept && !accept.split(",").some((a) => {
      const t = a.trim();
      if (t.endsWith("/*")) return f.type.startsWith(t.replace("/*", "/"));
      return f.type === t || f.name.toLowerCase().endsWith(t.replace(/^\./, "").toLowerCase());
    })) {
      // wrong type — ignore silently; user will see the visual reset
      return;
    }
    onFile(f);
  };
  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };
  return (
    <div
      className="col-span-2"
      onDragEnter={(e) => { stop(e); setDrag(true); }}
      onDragOver={(e) => { stop(e); setDrag(true); }}
      onDragLeave={(e) => { stop(e); setDrag(false); }}
      onDrop={onDrop}
      data-testid={testid ? `${testid}-dropzone` : undefined}
    >
      <label className="text-xs text-gray-500 uppercase tracking-wider mb-1 block">{label} (URL, upload, or drop a file)</label>
      <div className={`flex gap-2 rounded transition-colors ${drag ? "ring-2 ring-amber-400/60 bg-amber-400/5" : ""}`}>
        <input placeholder={drag ? "Drop to upload…" : "URL"} className="input-dark flex-1" value={url || ""} onChange={e => onUrl(e.target.value)} />
        <label className="btn-glass px-4 py-3 rounded cursor-pointer flex items-center gap-2 text-sm whitespace-nowrap">
          <Upload className="w-4 h-4" /> {loading ? "Uploading…" : "Upload"}
          <input data-testid={testid} type="file" accept={accept} hidden onChange={(e) => onFile(e.target.files?.[0])} />
        </label>
      </div>
      {!url && !loading && (
        <p className="text-[10px] text-gray-600 mt-1">Tip: drag an image from your desktop directly onto this field.</p>
      )}
    </div>
  );
}

export function SubtitleField({ subtitles = [], loading, onUpload, onRemove }) {
  return (
    <div className="col-span-2">
      <label className="text-xs text-gray-500 uppercase tracking-wider mb-2 block">Subtitles (.vtt or .srt)</label>
      <div className="flex flex-wrap gap-2 mb-2">
        {subtitles.map(s => (
          <span key={s.lang} className="bg-[#1a0808] border border-[#E50914]/30 text-xs px-3 py-1 rounded flex items-center gap-2">
            {s.label || s.lang}
            <button type="button" onClick={() => onRemove(s.lang)} className="text-gray-400 hover:text-red-400">×</button>
          </span>
        ))}
        {subtitles.length === 0 && <span className="text-gray-500 text-xs">No subtitles uploaded yet.</span>}
      </div>
      <div className="grid grid-cols-12 gap-2">
        <select id="sub-lang-select" className="input-dark col-span-4 text-sm" defaultValue="en">
          {LANGS.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
        </select>
        <label className="btn-glass col-span-8 px-4 py-3 rounded cursor-pointer flex items-center justify-center gap-2 text-sm">
          <Upload className="w-4 h-4" /> {loading ? "Uploading…" : "Upload .vtt for selected language"}
          <input data-testid="upload-subtitle" type="file" accept=".vtt,.srt,text/vtt" hidden onChange={(e) => {
            const lang = document.getElementById("sub-lang-select").value;
            onUpload("subtitle", e.target.files?.[0], lang);
          }} />
        </label>
      </div>
    </div>
  );
}

export function AudioTracksField({ tracks = [], loading, onUpload, onRemove }) {
  return (
    <div className="col-span-2">
      <label className="text-xs text-gray-500 uppercase tracking-wider mb-2 block">Alternate Audio Tracks (.mp3 / .m4a / .aac / .ogg)</label>
      <div className="flex flex-wrap gap-2 mb-2">
        {tracks.map(a => (
          <span key={a.lang} data-testid={`audio-chip-${a.lang}`} className="bg-[#08141a] border border-cyan-500/30 text-xs px-3 py-1 rounded flex items-center gap-2">
            🔊 {a.label || a.lang}
            <button type="button" onClick={() => onRemove(a.lang)} className="text-gray-400 hover:text-red-400">×</button>
          </span>
        ))}
        {tracks.length === 0 && <span className="text-gray-500 text-xs">No alternate audio uploaded. The main video's built-in audio will be used.</span>}
      </div>
      <div className="grid grid-cols-12 gap-2">
        <select id="audio-lang-select" className="input-dark col-span-4 text-sm" defaultValue="en">
          {LANGS.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
        </select>
        <label className="btn-glass col-span-8 px-4 py-3 rounded cursor-pointer flex items-center justify-center gap-2 text-sm">
          <Upload className="w-4 h-4" /> {loading ? "Uploading…" : "Upload audio for selected language"}
          <input data-testid="upload-audio" type="file" accept="audio/*,.mp3,.m4a,.aac,.ogg,.opus" hidden onChange={(e) => {
            const lang = document.getElementById("audio-lang-select").value;
            onUpload("audio", e.target.files?.[0], lang);
          }} />
        </label>
      </div>
    </div>
  );
}
